import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import Select from "react-select";
import "./StatusReport.css";
import {
    FaTicketAlt,
    FaCheckCircle,
    FaClock,
    FaPercent,
} from 'react-icons/fa';
const primary = {
    50: "#f3f1ff",
    100: "#e9e5ff",
    200: "#d5cfff",
    300: "#b7a9ff",
    400: "#9478ff",
    500: "#7341ff",
    600: "#631bff",
    700: "#611bf8",
    800: "#4607d0",
    900: "#3c08aa",
    950: "#220174",
    DEFAULT: "#611bf8",
    baseValue: "#611BF8"
};

const neutral = {
    50: "#f7f7f7",
    100: "#eeeeee",
    200: "#e0e0e0",
    300: "#cacaca",
    400: "#b1b1b1",
    500: "#999999",
    600: "#7f7f7f",
    700: "#676767",
    800: "#545454",
    900: "#464646",
    950: "#282828",
    baseValue: "#FFFFFF"
};


const StatusReport = () => {
    const formatDate = (date) => date.toISOString().split("T")[0];
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const [supportCenterOptions, setSupportCenterOptions] = useState([]);
    const [subcategoriesMap, setSubcategoriesMap] = useState({});
    const [reportData, setReportData] = useState({
        daily: { categories: [], raised: [], resolved: [] },
        weekly: { categories: [], raised: [], resolved: [] },
        monthly: { categories: [], raised: [], resolved: [] },
        metrics: { totalTickets: 0, resolved: 0, pending: 0, resolutionRate: 0 }
    });

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_API_TICKET_DROPDOWN, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
                    }
                });

                const data = await response.json();

                if (data.success) {
                    const categoryList = data.data.categories.map(cat => ({
                        label: cat.name,
                        value: cat.id
                    }));

                    const centerList = Object.entries(data.data.academies).map(([id, name]) => ({
                        label: name,
                        value: id
                    }));

                    const subMap = {};
                    data.data.categories.forEach(cat => {
                        subMap[cat.id] = cat.subcategories.map(sub => ({
                            label: sub.name,
                            value: sub.id
                        }));
                    });

                    setCategoryOptions(categoryList);
                    setSupportCenterOptions(centerList);
                    setSubcategoriesMap(subMap);
                }
            } catch (error) {
                console.error("Dropdown fetch failed:", error);
            }
        };

        fetchDropdownData();
    }, []);

    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 29);

    const defaultFromDate = formatDate(thirtyDaysAgo);
    const defaultToDate = formatDate(today);

    const [fromDateFilter, setFromDateFilter] = useState(defaultFromDate);
    const [toDateFilter, setToDateFilter] = useState(defaultToDate);


    const handleFromDateChange = (e) => {
        setFromDateFilter(e.target.value);
    };

    const handleToDateChange = (e) => {
        setToDateFilter(e.target.value);
    };

    const calculateDayDifference = () => {
        const from = new Date(fromDateFilter);
        const to = new Date(toDateFilter);
        const diffTime = Math.abs(to - from);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    const getViewType = () => {
        const dayDiff = calculateDayDifference();
        if (dayDiff <= 31) return "day";
        if (dayDiff <= 95) return "week";
        return "month";
    };

    const generateCategories = () => {
        const type = getViewType();
        if (type === "day") {
            const dayDiff = calculateDayDifference();
            return reportData.daily.categories.slice(-dayDiff);
        }
        if (type === "week") return reportData.weekly.categories;
        return reportData.monthly.categories;
    };
    const generateSeriesData = () => {
        const type = getViewType();
        const dayDiff = calculateDayDifference();
        let raised, resolved;

        if (type === "day") {
            raised = reportData.daily.raised.slice(-dayDiff);
            resolved = reportData.daily.resolved.slice(-dayDiff);
        } else if (type === "week") {
            raised = reportData.weekly.raised;
            resolved = reportData.weekly.resolved;
        } else {
            raised = reportData.monthly.raised;
            resolved = reportData.monthly.resolved;
        }

        return [
            { name: "Tickets Raised", data: raised },
            { name: "Tickets Resolved", data: resolved }
        ];
    };



    const chartOptions = {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'Inter, sans-serif' },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        colors: [primary[600], "#16a34a"],
        fill: {
            type: "gradient",
            gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.2, stops: [0, 90, 100] }
        },
        xaxis: {
            categories: generateCategories(),
            labels: {
                style: { colors: neutral[600], fontSize: '12px' },
                rotate: generateCategories().length > 15 ? -45 : 0
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: { labels: { style: { colors: neutral[600], fontSize: '12px' } } },
        tooltip: { style: { fontFamily: 'Inter, sans-serif' } },
        legend: { position: "top", horizontalAlign: "right", fontFamily: 'Inter, sans-serif', markers: { radius: 4 }, itemMargin: { horizontal: 16 } },
        grid: { borderColor: neutral[200], strokeDashArray: 4, padding: { top: 0, right: 0, bottom: 0, left: 0 } }
    };
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedCenter, setSelectedCenter] = useState(null);


    const fetchReportData = async () => {
        const academyIds = selectedCenter?.value;
        const categoryId = selectedCategory?.value;
        const subCategoryId = selectedSubcategory?.value;

        const url = new URL(
            process.env.REACT_APP_API_TICKET_STATUS_REPORT
        );
        const params = url.searchParams;
        params.set("startDate", fromDateFilter);
        params.set("endDate", toDateFilter);
        if (academyIds) params.set("academyIds", academyIds);
        if (categoryId) params.set("categoryId", categoryId);
        if (subCategoryId) params.set("subCategoryId", subCategoryId);

        try {
            const response = await fetch(url.toString(), {
                headers: {
                    "Content-Type": "application/json",
                    "x-authorization": process.env.REACT_APP_AUTH_TOKEN
                }
            });
            const data = await response.json();

            if (data.success) {
                setReportData({
                    daily: data.data.daily || { categories: [], raised: [], resolved: [] },
                    weekly: data.data.weekly || { categories: [], raised: [], resolved: [] },
                    monthly: data.data.monthly || { categories: [], raised: [], resolved: [] },
                    metrics: data.data.metrics || { totalTickets: 0, resolved: 0, pending: 0, resolutionRate: 0 }
                });
            } else {
                console.error("API Error:", data.success_message);
            }
        } catch (error) {
            console.error("Failed to fetch report data:", error);
        }
    };

    useEffect(() => {
        fetchReportData();
    }, [fromDateFilter, toDateFilter]);

    const stats = [
        {
            key: 'totalTickets',
            label: 'Total Tickets',
            icon: <FaTicketAlt />,
            color: '#28a745',
        },
        {
            key: 'resolved',
            label: 'Resolved',
            icon: <FaCheckCircle />,
            color: '#007bff',
        },
        {
            key: 'pending',
            label: 'Pending',
            icon: <FaClock />,
            color: '#fd7e14',
        },
        {
            key: 'resolutionRate',
            label: 'Resolution Rate',
            icon: <FaPercent />,
            color: '#6f42c1',
            suffix: '%',
        },
    ];


    return (
        <div className="status-report-container">
            <div className="sales-filters-login">
                <div className="floating-select">
                    <input
                        type="date"
                        className={`sales-filter-academy has-value`}
                        value={fromDateFilter}
                        onChange={handleFromDateChange}
                        onClick={(e) => e.target.showPicker()}
                        max={new Date().toISOString().split('T')[0]}
                    />
                    <label htmlFor="fromDateFilter">From Date</label>
                </div>

                <div className="floating-select">
                    <input
                        type="date"
                        className={`sales-filter-academy has-value`}
                        value={toDateFilter}
                        onChange={handleToDateChange}
                        onClick={(e) => e.target.showPicker()}
                        max={new Date().toISOString().split('T')[0]}
                    />
                    <label htmlFor="toDateFilter">To Date</label>
                </div>

                <div className="floating-select">
                    <Select
                        name="category"
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={(selected) => {
                            setSelectedCategory(selected);
                            setSelectedSubcategory(null);
                        }}
                        classNamePrefix="react-select"
                        className={`sales-filter-court-booking ${selectedCategory ? "has-value" : ""}`}
                        placeholder="Choose Category"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                            placeholder: (provided) => ({
                                ...provided,
                                color: 'black',
                            }),
                        }}
                    />
                    {selectedCategory && <label>Category</label>}
                </div>


                <div className="floating-select">
                    <Select
                        name="subcategory"
                        options={
                            selectedCategory?.value && subcategoriesMap[selectedCategory.value]
                                ? subcategoriesMap[selectedCategory.value]
                                : []
                        }
                        value={selectedSubcategory}
                        onChange={setSelectedSubcategory}
                        classNamePrefix="react-select"
                        className={`sales-filter-court-booking ${selectedSubcategory ? "has-value" : ""}`}
                        placeholder="Subcategory"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                            placeholder: (provided) => ({
                                ...provided,
                                color: 'black',
                            }),
                        }}
                    />
                    {selectedSubcategory && <label>Subcategory</label>}
                </div>


                <div className="floating-select">
                    <Select
                        name="supportCenter"
                        options={supportCenterOptions}
                        value={selectedCenter}
                        onChange={setSelectedCenter}
                        classNamePrefix="react-select"
                        className={`sales-filter-court-booking ${selectedCenter ? "has-value" : ""}`}
                        placeholder="Center"
                        isClearable
                        styles={{
                            placeholder: (provided) => ({
                                ...provided,
                                color: 'black',
                            }),
                        }}
                        menuPortalTarget={document.body}
                    />
                    {selectedCenter && <label>Center</label>}

                </div>
                <div className="floating-select">
                    <button className="analytics-apply-button" onClick={fetchReportData}  >
                        <span className="material-symbols-outlined filter-action-icon">filter_alt</span>
                        Apply Filters
                    </button>
                </div>
            </div>

            <div className="str-insight-hub-grid">
                {stats.map(({ key, label, icon, color, suffix }) => (
                    <div
                        key={key}
                        className="str-metric-strip"
                        style={{ borderTop: `4px solid ${color}` }}
                    >
                        <div className="str-metric-strip-header">
                            {icon && (
                                <span className="str-metric-icon" style={{ color }}>
                                    {icon}
                                </span>
                            )}
                            {label}
                        </div>
                        <div className="str-metric-strip-value">
                            {reportData.metrics[key].toLocaleString?.() ?? reportData.metrics[key]}
                            {suffix}
                        </div>
                    </div>
                ))}
            </div>

            <div className="sr-charts-grid grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="sr-trend-chart bg-white rounded-xl shadow-sm p-6 lg:col-span-2 hover:shadow-md transition-shadow border border-gray-100">
                    <div className="sr-chart-header flex justify-between items-center mb-6">
                        <h2 className="sr-chart-title text-lg font-semibold text-gray-900">Ticket Trend</h2>
                        <div className="view-type-indicator">
                            Showing: {getViewType().toUpperCase()} view
                        </div>
                    </div>
                    <div className="sr-chart-container h-[350px]">
                        <ReactApexChart
                            type="area"
                            height={350}
                            series={generateSeriesData()}
                            options={chartOptions}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatusReport;